import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Chip,
  Button,
  CircularProgress,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import GetAppIcon from '@material-ui/icons/GetApp';
import PersonIcon from '@material-ui/icons/Person';
import MapIcon from '@material-ui/icons/Map';
import TimerIcon from '@material-ui/icons/Timer';
import StorageIcon from '@material-ui/icons/Storage';
import MatchTimeline from './MatchTimeline';
import StatsCard from '../shared/StatsCard';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  header: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(2)
  },
  paper: {
    padding: theme.spacing(3)
  },
  section: {
    marginTop: theme.spacing(3)
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.grey[50]
    }
  },
  infoLabel: {
    fontWeight: 500,
    color: theme.palette.text.secondary
  },
  infoValue: {
    fontFamily: 'monospace'
  },
  players: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: theme.spacing(3)
  },
  player: {
    textAlign: 'center'
  },
  vs: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    fontWeight: 700,
    fontSize: '1.5rem'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400
  }
}));

const MatchDetails = ({ 
  matchId, 
  matchData,
  loading,
  onBack,
  onExport
}) => {
  const classes = useStyles();
  
  if (loading || !matchData) {
    return (
      <Container className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }
  
  if (!matchData.exists) {
    return (
      <Container className={classes.root}>
        <Paper className={classes.paper}>
          <Typography variant="h6">Match Not Found</Typography>
          <Typography color="textSecondary">
            Match ID: {matchId}
          </Typography>
          <Box mt={2}>
            <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
              Back to Dashboard
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }
  
  const { match, lifecycle, logs } = matchData;
  
  const getStatusColor = (status) => {
    if (status === 'FINISHED') return 'success';
    if (status === 'CANCELLED') return 'error';
    if (status === 'STARTED') return 'primary';
    return 'default';
  };
  
  return (
    <Container className={classes.root} maxWidth="xl">
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={onBack}
            variant="outlined"
          >
            Back
          </Button>
          <Typography variant="h4">
            Match Details
          </Typography>
          <Chip 
            label={match.status} 
            color={getStatusColor(match.status)}
          />
        </Box>
        
        <Box className={classes.headerActions}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={() => onExport(matchId)}
          >
            Export Debug Data
          </Button>
        </Box>
      </Box>
      
      {/* Match Info Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Match Duration"
            value={match.matchDuration 
              ? `${Math.floor(match.matchDuration / 60)}m`
              : 'In Progress'
            }
            subtitle={match.matchDuration 
              ? `${match.matchDuration} seconds`
              : ''
            }
            icon={<TimerIcon />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Lifecycle Events"
            value={lifecycle.summary?.totalEvents || 0}
            subtitle={`${lifecycle.summary?.errors || 0} errors`}
            icon={<StorageIcon />}
            color={lifecycle.summary?.errors > 0 ? 'error' : 'success'}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Log Entries"
            value={logs.totalLogs || 0}
            subtitle="Structured logs"
            icon={<StorageIcon />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Agent IP"
            value={match.agentIp 
              ? match.agentIp.split('.').pop()
              : 'N/A'
            }
            subtitle={match.agentIp || 'Not assigned'}
            icon={<StorageIcon />}
            color="primary"
          />
        </Grid>
      </Grid>
      
      {/* Match Information */}
      <Box className={classes.section}>
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Match Information
          </Typography>
          
          {/* Players */}
          <Box className={classes.players}>
            <Box className={classes.player}>
              <PersonIcon color="primary" style={{ fontSize: 40 }} />
              <Typography variant="h6">
                {match.player1?.userName || 'Player 1'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                ELO: {match.player1?.elo || 'N/A'}
              </Typography>
            </Box>
            
            <Box className={classes.vs}>VS</Box>
            
            <Box className={classes.player}>
              <PersonIcon color="secondary" style={{ fontSize: 40 }} />
              <Typography variant="h6">
                {match.player2?.userName || 'Player 2'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                ELO: {match.player2?.elo || 'N/A'}
              </Typography>
            </Box>
          </Box>
          
          <Divider />
          
          {/* Match Details */}
          <Box mt={2}>
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Match ID</Typography>
              <Typography className={classes.infoValue}>{matchId}</Typography>
            </Box>
            
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Map</Typography>
              <Typography className={classes.infoValue}>
                <MapIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {match.map}
              </Typography>
            </Box>
            
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Location</Typography>
              <Typography className={classes.infoValue}>{match.location}</Typography>
            </Box>
            
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Created At</Typography>
              <Typography className={classes.infoValue}>
                {match.createdAt 
                  ? new Date(match.createdAt * 1000).toLocaleString()
                  : 'N/A'
                }
              </Typography>
            </Box>
            
            {match.winnerId && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Winner</Typography>
                <Typography className={classes.infoValue}>
                  {match.winnerId === match.player1?._id 
                    ? match.player1?.userName
                    : match.player2?.userName
                  }
                </Typography>
              </Box>
            )}
            
            {match.reason && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Reason</Typography>
                <Typography className={classes.infoValue}>{match.reason}</Typography>
              </Box>
            )}
            
            {match.agentIp && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Agent IP</Typography>
                <Typography className={classes.infoValue}>{match.agentIp}</Typography>
              </Box>
            )}
            
            {logs.correlationId && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Correlation ID</Typography>
                <Typography className={classes.infoValue}>
                  {logs.correlationId.substring(0, 16)}...
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
      
      {/* Timeline */}
      <Box className={classes.section}>
        <MatchTimeline timeline={lifecycle.timeline} matchId={matchId} />
      </Box>
      
      {/* Lifecycle Summary */}
      {lifecycle.summary && lifecycle.summary.exists && (
        <Box className={classes.section}>
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Lifecycle Summary
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h4" color="primary">
                    {lifecycle.summary.totalEvents}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Total Events
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h4" color="secondary">
                    {lifecycle.summary.stateTransitions?.length || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    State Transitions
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2}>
                  <Typography 
                    variant="h4" 
                    style={{ 
                      color: lifecycle.summary.errors > 0 ? '#f44336' : '#4caf50' 
                    }}
                  >
                    {lifecycle.summary.errors || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Errors
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h4" color="primary">
                    {lifecycle.summary.durationFormatted || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Total Duration
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            {lifecycle.summary.errorDetails && lifecycle.summary.errorDetails.length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Errors Encountered:
                </Typography>
                {lifecycle.summary.errorDetails.map((error, index) => (
                  <Box 
                    key={index} 
                    p={1} 
                    mb={1} 
                    bgcolor="#ffebee" 
                    borderRadius={1}
                  >
                    <Typography variant="caption" style={{ fontFamily: 'monospace' }}>
                      {formatTimestamp(error.timestamp)}: {error.message || error.reason}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default MatchDetails;

